import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Store, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = window.location.origin + '/api';

export default function AcceptInvite() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  useEffect(() => {
    axios.get(`${API}/invites/${token}`)
      .then(r => {
        setInvite(r.data);
        if (r.data.email) setForm(f => ({ ...f, email: r.data.email }));
      })
      .catch(err => setError(err.response?.data?.detail || 'Invalid or expired invite'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (form.password.length < 8) {
      setSubmitError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/invites/${token}/accept`, form, { withCredentials: true });
      await checkAuth();
      setDone(true);
      setTimeout(() => navigate('/admin'), 1200);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Could not accept invite');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg,#0a1f44 0%,#1e3a8a 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} data-testid="accept-invite-page">
      <div style={{
        background: 'white', maxWidth: 460, width: '100%', borderRadius: 16, padding: 32,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {loading && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
            <Loader2 size={22} style={{ animation: 'spin 0.8s linear infinite' }} />
            <p style={{ marginTop: 12 }}>Validating your invite…</p>
          </div>
        )}

        {!loading && error && (
          <div style={{ textAlign: 'center', padding: 12 }} data-testid="invite-error">
            <div style={{ background: '#FEE2E2', color: '#991B1B', width: 60, height: 60, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <AlertCircle size={28} />
            </div>
            <h2 style={{ margin: '0 0 6px', color: 'var(--navy)' }}>Invite not valid</h2>
            <p style={{ color: 'var(--text-muted)' }}>{error}</p>
            <button onClick={() => navigate('/')} style={{ marginTop: 14, padding: '10px 20px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
              Back to StoreRuns
            </button>
          </div>
        )}

        {!loading && invite && !done && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ background: '#b6e34d', color: '#0a1f44', width: 60, height: 60, borderRadius: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <Store size={28} />
              </div>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: 0.08, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
                You&apos;ve been invited to admin
              </div>
              <h1 style={{ margin: 0, fontSize: '1.6rem', color: 'var(--navy)', letterSpacing: '-0.01em' }}>
                {invite.supermarket_name}
              </h1>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 8 }}>
                Create your admin account below. You&apos;ll get access to manage products, categories, and orders — scoped to {invite.supermarket_name} only.
              </p>
            </div>

            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={fieldLabel}>Your name</span>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  data-testid="invite-name" placeholder="e.g. John Banda" style={inputStyle} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={fieldLabel}>Email</span>
                <input required type="email" value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  data-testid="invite-email" placeholder="you@yourstore.com" style={inputStyle}
                  disabled={!!invite.email} />
                {invite.email && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    Locked to the address this invite was sent to.
                  </span>
                )}
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={fieldLabel}>Set a password (min 8 chars)</span>
                <input required type="password" value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  data-testid="invite-password" placeholder="Choose a strong password" style={inputStyle} minLength={8} />
              </label>
              {submitError && (
                <div data-testid="invite-submit-error" style={{ padding: 10, background: '#FEF2F2', color: '#991B1B', borderRadius: 8, fontSize: '0.82rem' }}>
                  {submitError}
                </div>
              )}
              <button type="submit" disabled={submitting} data-testid="invite-submit"
                style={{ padding: '12px 20px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.95rem', cursor: submitting ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: submitting ? 0.7 : 1, marginTop: 6 }}>
                {submitting ? <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> : null}
                {submitting ? 'Creating account…' : 'Create my admin account'}
              </button>
              <p style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                By continuing you agree to the StoreRuns Merchant Onboarding Terms.
              </p>
            </form>
          </>
        )}

        {done && (
          <div style={{ textAlign: 'center', padding: 12 }} data-testid="invite-success">
            <div style={{ background: '#D1FAE5', color: '#065F46', width: 60, height: 60, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <CheckCircle size={28} />
            </div>
            <h2 style={{ margin: '0 0 6px', color: 'var(--navy)' }}>Welcome aboard! 🎉</h2>
            <p style={{ color: 'var(--text-muted)' }}>Redirecting to your admin dashboard…</p>
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const inputStyle = {
  padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-gray)',
  fontSize: '0.92rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};

const fieldLabel = { fontSize: '0.78rem', fontWeight: 600, color: '#475569' };
